<template>
  <div class="loading_spinner" v-if="chkRain === false">
    <div class="loading_circle"></div>
  </div>
  <div
    :id="chartId"
    v-if="chkRain === true"
    :style="{
      background: 'var(--background-chart)',
      borderRadius: isLargeScreen ? '10px' : '0'
    }"
  ></div>
</template>

<script>
import { ref, onMounted, computed, nextTick, getCurrentInstance, watch } from 'vue';
import zingchart from 'zingchart';
import 'zingchart/modules-es6/zingchart-treemap.min.js';
import { mapState, useStore } from 'vuex';

export default {
  name: 'RainbowChart',
  setup() {
    const { proxy } = getCurrentInstance();
    const chartId = ref('zingchart-' + Math.random().toString(36).substr(2, 9));
    const store = useStore();

    const rainbowData = computed(() => store.state.indicator.chartRainbow);
    const language = computed(() => store.state.dominance.lang);

    // 현재 화면 크기
    const isLargeScreen = ref(window.innerWidth > 1200);

    window.addEventListener('resize', () => {
      isLargeScreen.value = window.innerWidth > 1200;
    });

    const getTranslatedText = computed(() => ({
      bonanza: proxy.$t('indi.bon'),
      bail: proxy.$t('indi.ready'),
      frenzy: proxy.$t('indi.frenzy'),
      stash: proxy.$t('indi.stash'),
      buy: proxy.$t('indi.buy'),
      sale: proxy.$t('indi.sale'),
      bitcoin: proxy.$t('pub.btcPrice'),
    }));

    const chartData = ref({
      type: "mixed",
      globals: {
        fontFamily: 'var(--main-font-Light), "Noto Sans JP", "Noto Sans SC"',
        fontColor: "var(--color-main)",
        fontSize: '15px',
      },
      gui: {
        contextMenu: {
          visible: false
        }
      },
      backgroundColor: "transparent",
      legend: {
        align: 'center',
        backgroundColor: 'transparent',
        borderColor: 'none',
        layout: isLargeScreen.value ? 'x2' : 'x7',
        shadow: false,
        item: {
          fontColor: '#FFFFFF',
        },
        marker: {
          'border-color': 'none',
          size: '8px',
        },
      },
      plot: {
        tooltip: {
          visible: false,
        },
        alphaArea: 1,
        aspect: 'spline',
        hoverState: {
          backgroundColor: 'none',
        },
        lineWidth: '0px',
        marker: {
          visible: false,
        },
      },
      scaleX: {
        values: [],
        guide: {
          visible: false,
        },
      },
      scaleY: {
        values: [0.01, 0.1, 1, 10, 100, 1000, 10000, 100000, 1000000],
        guide: {
          visible: false,
        },
        progression: "log",
      },
      crosshairX: {
        lineColor: '#FFFFFF',
        plotLabel: {
          backgroundColor: "var(--background-chart2)",
          fontFamily: 'var(--main-font-Light), "Noto Sans JP", "Noto Sans SC"',
          text: "%t: $ %v",
          "thousands-separator": ","
        },
        lineWidth: '1px',
        borderWidth: 1,
        marker: {
          visible: false,
        },
        offsetY: '10%',
      },
      series: [
        {
          type: "range",
          text: getTranslatedText.value.bonanza,
          scales: "scaleX, scaleY",
          values: [],
          backgroundColor: 'var(--chart-red)',
          lineColor: 'var(--chart-red)',
          valueRange: true,
        },
        {
          type: "range",
          text: getTranslatedText.value.bail,
          scales: "scaleX, scaleY",
          values: [],
          backgroundColor: 'var(--chart-yellow)',
          lineColor: 'var(--chart-yellow)',
          valueRange: true,
        },
        {
          type: "range",
          text: getTranslatedText.value.frenzy,
          scales: "scaleX, scaleY",
          values: [],
          backgroundColor: 'var(--rainbow-yellow)',
          lineColor: 'var(--rainbow-yellow)',
          valueRange: true,
        },
        {
          type: "range",
          text: getTranslatedText.value.stash,
          scales: "scaleX, scaleY",
          values: [],
          backgroundColor: 'var(--text-green)',
          lineColor: 'var(--text-green)',
          valueRange: true,
        },
        {
          type: "range",
          text: getTranslatedText.value.buy,
          scales: "scaleX, scaleY",
          values: [],
          backgroundColor: 'var(--scrole-color)',
          lineColor: 'var(--scrole-color)',
          valueRange: true,
        },
        {
          type: "range",
          text: getTranslatedText.value.sale,
          scales: "scaleX, scaleY",
          values: [],
          backgroundColor: 'var(--chart-purple)',
          lineColor: 'var(--chart-purple)',
          valueRange: true,
        },
        {
          type: "line",
          text: getTranslatedText.value.bitcoin,
          scales: "scaleX, scaleY",
          "line-color": "#FFFFFF",
          "line-width": 3,
          values: [],
          marker: {
            "background-color": "#FFFFFF",
            "border-width": 2,
            shadow: 0,
            "border-color": "#1C1C1C",
          },
        },
      ],
    });

    const fontLang = () => {
      if (language.value === 'ja') {
        return "Noto Sans JP";
      } else if (language.value === 'zh') {
        return "Noto Sans SC";
      } else {
        return "var(--main-font-Light)";
      }
    };

    const renderChart = () => {
      zingchart.render({
        id: chartId.value,
        data: chartData.value,
        height: '500px',
        width: '100%',
      });

      nextTick(() => {
        zingchart.exec(chartId.value, 'resize');
      });
    };

    const updateChartData = (newData) => {
      chartData.value.legend.layout = isLargeScreen.value ? 'x7' : 'x2';
      chartData.value.globals.fontFamily = fontLang();
      chartData.value.scaleX.values = newData.timeValue;

      chartData.value.series[0].values = newData.max;
      chartData.value.series[1].values = newData.sell;
      chartData.value.series[2].values = newData.fomo;
      chartData.value.series[3].values = newData.acc;
      chartData.value.series[4].values = newData.buy;
      chartData.value.series[5].values = newData.fire;
      chartData.value.series[6].values = newData.btc;

      zingchart.exec(chartId.value, 'setdata', {
        data: chartData.value,
      });

      zingchart.exec(chartId.value, 'resize');
    };

    const removeBtn = async() => {
      nextTick(() => {
        const toolBar = document.querySelector(`#${chartId.value}-trigger-c`);
        const zingBtn = document.querySelectorAll('[id*="license-text"]');
        if (zingBtn.length > 0 && zingBtn[0]) {
          zingBtn[0].style.display = 'none';
        }
        if (toolBar) toolBar.style.display = 'none';
      });
    };

    const loadRainbowChart = async () => {
      await store.dispatch('indicator/loadRainbow');
      await nextTick();
      if (rainbowData.value) {
        updateChartData(rainbowData.value);
      }
      await removeBtn();
    };

    onMounted(async () => {
      await loadRainbowChart();
      renderChart();
    });

    watch(getTranslatedText, () => {
      chartData.value.series.forEach((s, idx) => {
        s.text = Object.values(getTranslatedText.value)[idx];
      });
      if (rainbowData.value) {
        updateChartData(rainbowData.value);
      }
      removeBtn();
    });

    watch(isLargeScreen, async() => {
      await nextTick();
      await loadRainbowChart();
    });

    return {
      chartId,
      isLargeScreen,
    };
  },
  computed: {
    ...mapState("indicator", ["chkRain"]),
  },
};
</script>

<style>
#chartId {
  width: 100%;
  height: 100%;
}
</style>
